/* eslint-disable react/require-default-props */
import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
   formatCurrency,
   formatMonthLabel,
   formatNumber,
   formatPercent,
} from '../utils/format';
import {
   PLOT,
   plottablePoints,
   toPercentX,
   toPercentY,
   xOf,
   yOf,
} from '../utils/vintage';
import {
   AxisLabel,
   ChartCaption,
   ChartFrame,
   ChartMarks,
   ChartTooltip,
} from '../styles';

// ---------------------------------------------------------------------------
// Gráfico de linhas da safra — SVG à mão, sem biblioteca.
//
// Uma linha por safra, X = meses decorridos após a emissão, Y = % do valor
// emitido que atingiu o limiar de atraso.
//
// Três regras carregam o significado:
//  · Ponto `null` NÃO é plotado nem interpolado. A curva de uma safra recente
//    termina antes das outras — é a informação, não uma falha.
//  · Safra de carga histórica vai TRACEJADA; emissão MWA, sólida.
//  · Clicar isola uma safra; as demais recuam para 18% de opacidade.
//
// ⚠️ O hover NÃO é feito com listener por bolinha. Com raio 4 e safras que se
// cruzam, o operador teria que acertar o centro de um alvo de 8px — e onde os
// pontos se encostam, o alvo de uma safra roubaria o da outra. Em vez disso há
// uma camada transparente sobre o plot que captura o movimento e resolve o
// PONTO MAIS PRÓXIMO do cursor. Alvo efetivo grande, sem sobreposição entre
// safras, e um listener em vez de centenas.
// ---------------------------------------------------------------------------

// Raio de captura, em unidades do viewBox (~1 unidade ≈ 1px na largura usual
// do card). Além disso o cursor está longe demais para adivinhar a intenção.
const HOVER_RADIUS = 26;

export default function VintageChart({
   safras,
   scale,
   months,
   isolated,
   onIsolate,
}) {
   const frameRef = useRef(null);
   const [hovered, setHovered] = useState(null);

   // Cada safra com seus pontos já projetados — serve para desenhar as linhas
   // e, achatado, para o teste de proximidade.
   const series = useMemo(
      () =>
         safras.map((safra) => ({
            ...safra,
            points: plottablePoints(safra, scale.max),
         })),
      [safras, scale.max],
   );

   const allPoints = useMemo(
      () =>
         series.flatMap((safra) =>
            safra.points.map((point) => ({ ...point, safra })),
         ),
      [series],
   );

   // A safra em destaque: o clique (isolamento) manda; na ausência dele, o
   // hover. Assim passar o mouse não desfaz uma safra isolada.
   const activeSafra = isolated ?? hovered?.safra?.safra ?? null;

   const findNearest = (event) => {
      const frame = frameRef.current;
      if (!frame || !allPoints.length) return null;

      const rect = frame.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;

      // Coordenadas do cursor no espaço do viewBox.
      const x = ((event.clientX - rect.left) / rect.width) * PLOT.width;
      const y = ((event.clientY - rect.top) / rect.height) * PLOT.height;

      let best = null;
      let bestDistance = Infinity;
      allPoints.forEach((point) => {
         const distance = Math.hypot(point.x - x, point.y - y);
         if (distance < bestDistance) {
            bestDistance = distance;
            best = point;
         }
      });

      return bestDistance <= HOVER_RADIUS ? best : null;
   };

   const handleMove = (event) => {
      const nearest = findNearest(event);
      setHovered((current) => {
         if (!nearest) return current ? null : current;
         if (
            current &&
            current.m === nearest.m &&
            current.safra.safra === nearest.safra.safra
         ) {
            return current;
         }
         return nearest;
      });
   };

   return (
      <div>
         <ChartFrame ref={frameRef}>
            <svg
               viewBox={`0 0 ${PLOT.width} ${PLOT.height}`}
               role="img"
               aria-label={`Percentual em atraso por safra ao longo dos meses após a emissão. ${safras.length} safras plotadas. Os valores exatos estão na tabela abaixo.`}
            >
               {/* Grade: linha sólida de 1px, um passo fora da superfície.
                   Tracejado aqui leria como projeção ou limite. */}
               {scale.ticks.map((tick) => (
                  <line
                     key={tick}
                     x1={PLOT.x0}
                     x2={PLOT.x1}
                     y1={yOf(tick, scale.max)}
                     y2={yOf(tick, scale.max)}
                     stroke="rgba(0, 0, 0, 0.08)"
                     strokeWidth="1"
                  />
               ))}

               <ChartMarks>
                  {series.map((safra) => {
                     if (!safra.points.length) return null;

                     const dimmed = activeSafra && activeSafra !== safra.safra;
                     const highlighted = activeSafra === safra.safra;

                     let strokeWidth = 2;
                     if (highlighted) strokeWidth = 3;
                     else if (dimmed) strokeWidth = 1.5;

                     return (
                        <g key={safra.safra} opacity={dimmed ? 0.18 : 1}>
                           <polyline
                              points={safra.points
                                 .map((point) => `${point.x},${point.y}`)
                                 .join(' ')}
                              fill="none"
                              stroke={safra.color}
                              strokeWidth={strokeWidth}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeDasharray={
                                 safra.isHistorical ? '5 4' : undefined
                              }
                           />
                           {safra.points.map((point) => {
                              const isHovered =
                                 hovered?.m === point.m &&
                                 hovered?.safra?.safra === safra.safra;
                              return (
                                 <circle
                                    key={point.m}
                                    cx={point.x}
                                    cy={point.y}
                                    r={isHovered ? 7 : 4}
                                    fill="#fff"
                                    stroke={safra.color}
                                    strokeWidth={isHovered ? 3 : 2}
                                 />
                              );
                           })}
                        </g>
                     );
                  })}
               </ChartMarks>

               {/* Camada de captura — por último para ficar acima de tudo. */}
               <rect
                  x={PLOT.x0 - HOVER_RADIUS}
                  y={0}
                  width={PLOT.x1 - PLOT.x0 + HOVER_RADIUS * 2}
                  height={PLOT.height}
                  fill="transparent"
                  style={{ cursor: hovered ? 'pointer' : 'default' }}
                  onMouseMove={handleMove}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => {
                     if (hovered) onIsolate(hovered.safra.safra);
                  }}
               />
            </svg>

            {/* Rótulos do eixo Y — múltiplos exatos do passo. */}
            {scale.ticks.map((tick) => (
               <AxisLabel
                  key={`y-${tick}`}
                  style={{
                     left: `${toPercentX(PLOT.x0 - 8)}%`,
                     top: `${toPercentY(yOf(tick, scale.max))}%`,
                     transform: 'translate(-100%, -50%)',
                  }}
               >
                  {formatPercent(tick, tick % 1 === 0 ? 0 : 1)}
               </AxisLabel>
            ))}

            {/* Rótulos do eixo X — um por mês pedido ao backend. */}
            {months.map((month) => (
               <AxisLabel
                  key={`x-${month}`}
                  style={{
                     left: `${toPercentX(xOf(month))}%`,
                     top: `${toPercentY(PLOT.yBase + 18)}%`,
                     transform: 'translateX(-50%)',
                  }}
               >
                  M+{month}
               </AxisLabel>
            ))}

            {hovered ? (
               <ChartTooltip
                  style={{
                     left: `${toPercentX(hovered.x)}%`,
                     top: `${toPercentY(hovered.y)}%`,
                     // Acima do ponto por padrão; do lado de dentro quando o
                     // ponto está perto da borda, para o tooltip não sair do
                     // card e ficar cortado.
                     transform: `translate(${
                        toPercentX(hovered.x) > 65 ? '-100%' : '-50%'
                     }, calc(-100% - 12px))`,
                  }}
               >
                  <strong>{formatMonthLabel(hovered.safra.safra)}</strong>
                  <span>
                     M+{hovered.m} · {formatPercent(hovered.pct, 2)} do emitido
                  </span>
                  <span>
                     {formatNumber(hovered.titulos)} títulos ·{' '}
                     {formatCurrency(hovered.valor)}
                  </span>
               </ChartTooltip>
            ) : null}
         </ChartFrame>

         <ChartCaption>
            Meses decorridos após a emissão · pontos futuros não são plotados.
         </ChartCaption>
      </div>
   );
}

VintageChart.propTypes = {
   safras: PropTypes.arrayOf(
      PropTypes.shape({
         safra: PropTypes.string,
         color: PropTypes.string,
         isHistorical: PropTypes.bool,
         pontos: PropTypes.arrayOf(PropTypes.shape({})),
      }),
   ).isRequired,
   scale: PropTypes.shape({
      max: PropTypes.number,
      step: PropTypes.number,
      ticks: PropTypes.arrayOf(PropTypes.number),
   }).isRequired,
   months: PropTypes.arrayOf(PropTypes.number).isRequired,
   isolated: PropTypes.string,
   onIsolate: PropTypes.func.isRequired,
};
